import { LogLevel } from '@/hooks/telemetry/use-seq-log';
import {
  getCurrentOwnerDestinationDepth,
  getHistorySentinelMetadata,
  isCurrentOwnerHistorySentinel,
} from './history-reconciler-state';
import type {
  ClosePriorityLayers,
  HistoryReconcilerLogger,
  HistorySentinelMetadata,
  ModalStackHistoryAdapter,
  MutableValueRef,
} from './history-reconciler-types';

interface PopstateReconciliationAction {
  nextBrowserDepth: number;
  nextPendingBrowserBack: number;
  suppressPopstateCount: number;
  historyGoDelta: number | null;
  layersToClose: number;
}

interface ResolvePopstateReconciliationInput {
  currentDesiredDepth: number;
  currentBrowserDepth: number;
  currentPendingBrowserBack: number;
  destinationSentinelMetadata: HistorySentinelMetadata | null;
  ownerId: string;
}

interface ResolveDepthPopstateInput {
  currentDesiredDepth: number;
  currentBrowserDepth: number;
  currentPendingBrowserBack: number;
  destinationDepth: number;
}

interface ConsumeSuppressedPopstateInput {
  suppressCountRef: MutableValueRef<number>;
  logger: HistoryReconcilerLogger;
}

interface PopstateLogInput {
  logger: HistoryReconcilerLogger;
  destinationSentinelMetadata: HistorySentinelMetadata | null;
}

interface PopstateReceivedLogInput extends PopstateLogInput {
  suppressCount: number;
}

interface PopstateProcessingLogInput extends PopstateLogInput {
  currentDesiredDepth: number;
  currentBrowserDepth: number;
  isCurrentOwnerSentinel: boolean;
}

interface ApplyPopstateReconciliationActionInput {
  action: PopstateReconciliationAction;
  browserDepthRef: MutableValueRef<number>;
  pendingBrowserBackRef: MutableValueRef<number>;
  suppressCountRef: MutableValueRef<number>;
  closePriorityLayers: ClosePriorityLayers;
}

export interface ProcessPopstateEventInput {
  event: PopStateEvent;
  ownerIdRef: MutableValueRef<string>;
  modalStackRef: MutableValueRef<ModalStackHistoryAdapter>;
  browserDepthRef: MutableValueRef<number>;
  pendingBrowserBackRef: MutableValueRef<number>;
  suppressCountRef: MutableValueRef<number>;
  closePriorityLayers: ClosePriorityLayers;
  logger: HistoryReconcilerLogger;
}

function getHistoryBackDelta(count: number): number | null {
  if (count <= 0) {
    return null;
  }

  return -count;
}

function resolveForeignSentinelAction(
  input: ResolvePopstateReconciliationInput,
): PopstateReconciliationAction | null {
  const destinationSentinelMetadata = input.destinationSentinelMetadata;
  if (!destinationSentinelMetadata || destinationSentinelMetadata.owner === input.ownerId) {
    return null;
  }

  return {
    nextBrowserDepth: input.currentDesiredDepth,
    nextPendingBrowserBack: 0,
    suppressPopstateCount: 1,
    historyGoDelta: -destinationSentinelMetadata.depth,
    layersToClose: 0,
  };
}

function resolveEmptyStackPopstateAction(
  input: ResolveDepthPopstateInput,
): PopstateReconciliationAction | null {
  if (input.currentDesiredDepth > 0) {
    return null;
  }

  return {
    nextBrowserDepth: 0,
    nextPendingBrowserBack: 0,
    suppressPopstateCount: Math.max(0, Math.sign(input.destinationDepth)),
    historyGoDelta: getHistoryBackDelta(input.destinationDepth),
    layersToClose: 0,
  };
}

function resolveForwardSentinelAction(
  input: ResolveDepthPopstateInput,
): PopstateReconciliationAction | null {
  if (input.destinationDepth < input.currentBrowserDepth) {
    return null;
  }

  const staleDepthDelta = input.destinationDepth - input.currentDesiredDepth;
  return {
    nextBrowserDepth: staleDepthDelta > 0 ? input.currentDesiredDepth : input.currentBrowserDepth,
    nextPendingBrowserBack: input.currentPendingBrowserBack,
    suppressPopstateCount: Math.max(0, Math.sign(staleDepthDelta)),
    historyGoDelta: getHistoryBackDelta(staleDepthDelta),
    layersToClose: 0,
  };
}

function resolveCloseLayersAction(input: ResolveDepthPopstateInput): PopstateReconciliationAction {
  return {
    nextBrowserDepth: input.destinationDepth,
    nextPendingBrowserBack: input.currentPendingBrowserBack,
    suppressPopstateCount: 0,
    historyGoDelta: null,
    layersToClose: input.currentDesiredDepth - input.destinationDepth,
  };
}

function resolvePopstateReconciliation(
  input: ResolvePopstateReconciliationInput,
): PopstateReconciliationAction {
  const foreignSentinelAction = resolveForeignSentinelAction(input);
  if (foreignSentinelAction) {
    return foreignSentinelAction;
  }

  const destinationDepth = getCurrentOwnerDestinationDepth({
    destinationSentinelMetadata: input.destinationSentinelMetadata,
    ownerId: input.ownerId,
  });
  const depthInput = {
    currentDesiredDepth: input.currentDesiredDepth,
    currentBrowserDepth: input.currentBrowserDepth,
    currentPendingBrowserBack: input.currentPendingBrowserBack,
    destinationDepth,
  };
  const emptyStackAction = resolveEmptyStackPopstateAction(depthInput);
  if (emptyStackAction) {
    return emptyStackAction;
  }

  const forwardSentinelAction = resolveForwardSentinelAction(depthInput);
  if (forwardSentinelAction) {
    return forwardSentinelAction;
  }

  return resolveCloseLayersAction(depthInput);
}

function logPopstateReceived(input: PopstateReceivedLogInput): void {
  input.logger("HRP-POP-1 popstate received", {
    suppressCount: input.suppressCount,
    url: `${window.location.pathname}${window.location.search}`,
    eventStateOwner: input.destinationSentinelMetadata?.owner ?? null,
  }, LogLevel.Warning);
}

function logPopstateProcessing(input: PopstateProcessingLogInput): void {
  input.logger("HRP-POP-3 popstate: NOT suppressed, processing", {
    currentDesiredDepth: input.currentDesiredDepth,
    browserDepth: input.currentBrowserDepth,
    destinationDepth: input.destinationSentinelMetadata?.depth ?? null,
    isCurrentOwnerSentinel: input.isCurrentOwnerSentinel,
  }, LogLevel.Warning);
}

function consumeSuppressedPopstate(input: ConsumeSuppressedPopstateInput): boolean {
  if (input.suppressCountRef.current <= 0) {
    return false;
  }

  input.suppressCountRef.current -= 1;
  input.logger("HRP-POP-2 popstate suppressed", {
    suppressCount: input.suppressCountRef.current,
  }, LogLevel.Info);
  return true;
}

function applyPopstateReconciliationAction(input: ApplyPopstateReconciliationActionInput): void {
  input.pendingBrowserBackRef.current = input.action.nextPendingBrowserBack;
  input.browserDepthRef.current = input.action.nextBrowserDepth;
  input.suppressCountRef.current += input.action.suppressPopstateCount;

  if (input.action.historyGoDelta != null) {
    window.history.go(input.action.historyGoDelta);
  }

  if (input.action.layersToClose > 0) {
    input.closePriorityLayers(input.action.layersToClose);
  }
}

export function processPopstateEvent(input: ProcessPopstateEventInput): void {
  const destinationSentinelMetadata = getHistorySentinelMetadata(input.event.state);
  logPopstateReceived({
    logger: input.logger,
    destinationSentinelMetadata,
    suppressCount: input.suppressCountRef.current,
  });
  if (consumeSuppressedPopstate({
    suppressCountRef: input.suppressCountRef,
    logger: input.logger,
  })) {
    return;
  }

  const currentDesiredDepth = input.modalStackRef.current.getDepthCount();
  const isCurrentOwnerSentinel = isCurrentOwnerHistorySentinel({
    destinationSentinelMetadata,
    ownerId: input.ownerIdRef.current,
  });
  logPopstateProcessing({
    logger: input.logger,
    destinationSentinelMetadata,
    currentDesiredDepth,
    currentBrowserDepth: input.browserDepthRef.current,
    isCurrentOwnerSentinel,
  });
  applyPopstateReconciliationAction({
    action: resolvePopstateReconciliation({
      currentDesiredDepth,
      currentBrowserDepth: input.browserDepthRef.current,
      currentPendingBrowserBack: input.pendingBrowserBackRef.current,
      destinationSentinelMetadata,
      ownerId: input.ownerIdRef.current,
    }),
    browserDepthRef: input.browserDepthRef,
    pendingBrowserBackRef: input.pendingBrowserBackRef,
    suppressCountRef: input.suppressCountRef,
    closePriorityLayers: input.closePriorityLayers,
  });
}
